import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import SkeletonTable from "../../components/table/SkeletonTable";
import columns from "../../columns/payroll/payroll";
import addPayrollColumns from "../../columns/payroll/addPayroll";
import {
  AddUpdatePayrollData,
  DeletePayrollEmployee,
  GetPayrollEmployeeData,
  UpdatePayrollEmployeeStatus,
} from "../../api/request/payroll/payroll";
import toast from "react-hot-toast";
import Eye from "../../assets/View";
import LoadableButton from "../../components/button/LoadableButton";
import Add from "../../assets/Add";
import AddEmployeeModal from "./components/AddEmployee";
import LeftArrow from "../../assets/LeftArrow";
import { Switch } from "antd";
import Edit from "../../assets/Edit";
import Delete from "../../assets/Delete";
import ConfirmationModal from "../../components/modals/ConfirmationModal";
import moment from "moment";
import GlobalTooltip from "../../components/common/GlobalTooltip";
import { useSelector } from "react-redux";
import { getCurrentDateByTimezone } from "../../utils/utils";
import dayjs from "dayjs";
import TimeZoneRangePicker from "../../components/common/TimeZoneRangePicker";

const PayRoll = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [addEmployee, setAddEmployee] = useState(false);
  const [mode, setMode] = useState("view");
  const [range, setRange] = useState([]);
  const [isEmpDelete, setIsEmpDelete] = useState(false);
  const [isEmpDeleteLoading, setIsEmpDeleteLoading] = useState(false);
  const [isUpdateLoading, setIsUpdateLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [statusChangeInfo, setStatusChangeInfo] = useState(null);
  const [activeUsers, setActiveUsers] = useState([]);
  const { userDetails } = useSelector((state) => state?.user);

  const timezoneToday = getCurrentDateByTimezone(
    userDetails?.time_zone || Intl.DateTimeFormat().resolvedOptions().timeZone
  );

  const disabledDate = (current) => {
    if (!current) return false;
    return current.isAfter(dayjs().endOf("month"), "day");
  };

  const getPayrollData = async () => {
    try {
      setIsLoading(true);
      const { data } = await GetPayrollEmployeeData();
      if (data?.status) {
        let employees = data?.data?.map((item) => {
          const isActive = item?.status === "active";

          return {
            ...item,
            rawStatus: item?.status,
            hours: item?.hours ?? 0,
            overtime_hour: item?.overtime_hour ?? 0,
            holidays: item?.holidays ?? 0,
            rate: item?.rate ?? 0,
            address: (
              <div className="w-[100px] truncate" title={item?.address}>
                {item?.address}
              </div>
            ),
            created_at: moment(item?.created_at).format("lll"),
            status: (
              <Switch
                checked={isActive}
                onChange={(checked) => {
                  setStatusChangeInfo({
                    employeeId: item?.id,
                    newStatus: checked ? "active" : "inactive",
                  });
                  setIsStatusModalOpen(true);
                }}
              />
            ),
            dob: item?.dob ? moment(item.dob).format("MMM DD, YYYY") : "--",
            joining_date: item?.dob
              ? moment(item.joining_date).format("MMM DD, YYYY")
              : "--",
            exemption: (
              <p
                className={` ${
                  item?.exemption === "yes"
                    ? "text-green-500 font-bold capitalize"
                    : item?.exemption === "no"
                    ? "text-red-600 font-bold capitalize"
                    : ""
                }`}
              >
                {item?.exemption ? item.exemption : "--"}
              </p>
            ),
            action: (
              <div className="flex gap-3">
                <GlobalTooltip title="View Employee Details">
                  <button
                    onClick={() =>
                      navigate(`/payroll/${item?.id}`, {
                        state: { employee: item },
                      })
                    }
                  >
                    <Eye />
                  </button>
                </GlobalTooltip>

                <GlobalTooltip title="Edit Employee Details">
                  <button
                    onClick={(e) => {
                      e?.stopPropagation();
                      setSelectedEmployee(item);
                      setAddEmployee(true);
                    }}
                  >
                    <Edit className="!size-4" />
                  </button>
                </GlobalTooltip>

                <GlobalTooltip title="Delete Employee">
                  <button
                    onClick={(e) => {
                      e?.stopPropagation();
                      setIsEmpDelete(true);
                      setSelectedEmployee(item);
                    }}
                  >
                    <Delete color="#000" className="!size-4" />
                  </button>
                </GlobalTooltip>
              </div>
            ),
          };
        });
        setData(employees);
        setActiveUsers([]);
        setAddEmployee(false);
      }

      setIsLoading(false);
    } catch (error) {
      if (error?.response?.data?.message)
        toast?.error(error?.response?.data?.message);
      setIsLoading(false);
    }
  };

  const handleUpdate = async () => {
    try {
      setIsUpdateLoading(true);
      if (!range?.length) {
        toast.error("Please select a date range before saving payroll");
        return;
      }

      const payload = {
        start_date: range[0].format("YYYY-MM-DD"),
        end_date: range[1].format("YYYY-MM-DD"),
        payroll_data: activeUsers?.map((item) => ({
          payroll_emp_id: item?.payroll_emp_id || item?.id,
          name: item?.name,
          hours: item?.hours || 0,
          overtime_hour: item?.overtime_hour || 0,
          holidays: item?.holidays || 0,
          rate: item?.rate || 0,
        })),
      };

      const { data: res } = await AddUpdatePayrollData(payload);

      if (res?.status) {
        toast.success(res?.message || "Payroll updated successfully");
        setMode("view");
        setAddEmployee(false);
        setActiveUsers([]);
        getPayrollData();
      } else {
        toast.error(res?.message || "Payroll update failed");
      }
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.message || "Error submitting payroll");
    } finally {
      setIsUpdateLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setIsEmpDeleteLoading(true);
      const { data } = await DeletePayrollEmployee({
        id: selectedEmployee?.id,
      });
      if (data?.status) {
        toast?.success(data?.message || "Employee deleted successfully");
        setIsEmpDelete(false);
        getPayrollData();
      }
    } catch (error) {
      toast?.error(error?.response?.data?.message || "Error deleting employee");
    } finally {
      setIsEmpDeleteLoading(false);
    }
  };

  const handleStatusConfirm = async (info) => {
    if (!info) return;
    try {
      setStatusLoading(true);
      const payload = {
        employee_id: info.employeeId,
        status: info.newStatus,
      };
      const { data } = await UpdatePayrollEmployeeStatus(payload);

      if (data?.status) {
        toast.success(data?.message || "Status updated successfully");
        getPayrollData();
      } else {
        toast.error(data?.message || "Failed to update status");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Error updating status");
    } finally {
      setStatusLoading(false);
      setIsStatusModalOpen(false);
    }
  };

  useEffect(() => {
    getPayrollData();
  }, []);

  useEffect(() => {
    setActiveUsers(data?.filter((user) => user?.rawStatus === "active"));
  }, [data]);

  return (
    <>
      <div className="border rounded-xl bg-white">
        {mode === "view" ? (
          <div>
            <div className="flex justify-between mx-5 mt-5">
              <p className="title">
                Employee {data?.length ? `(${data?.length})` : ""}
              </p>
              <div className="flex gap-3 items-center">
                <LoadableButton
                  className="btn btn-primary !px-4"
                  onClick={() => setAddEmployee(true)}
                >
                  Add Employee
                </LoadableButton>
                <LoadableButton
                  className="btn btn-primary !px-4"
                  onClick={() => setMode("add")}
                >
                  <Add />
                  Add Payroll
                </LoadableButton>
                <LoadableButton
                  className="btn btn-primary !px-4"
                  onClick={() => navigate("/payroll/view_payroll")}
                >
                  View Payroll
                </LoadableButton>
              </div>
            </div>
            <div className="mt-5">
              <SkeletonTable
                columns={columns}
                data={data}
                isLoading={isLoading}
              />
            </div>

            <AddEmployeeModal
              open={addEmployee}
              setOpen={setAddEmployee}
              employee={selectedEmployee}
              setEmployee={setSelectedEmployee}
              getPayrollData={getPayrollData}
              timezoneToday={timezoneToday}
            />

            <ConfirmationModal
              isOpen={isEmpDelete}
              setIsOpen={setIsEmpDelete}
              message="Are you sure you want to delete this employee?"
              onConfirm={handleDelete}
              isLoading={isEmpDeleteLoading}
              loadingLabel="Deleting Employee..."
              type="warning"
            />
          </div>
        ) : (
          <>
            <div className="flex justify-between gap-2 mx-5 mt-5">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setMode("view")}
                  className="text-secondary"
                >
                  <LeftArrow />
                </button>
                <p className="title">Add Payroll</p>
              </div>
              <TimeZoneRangePicker
                setRange={setRange}
                disabledDate={disabledDate}
                timezoneToday={timezoneToday}
              />
            </div>
            <div className="my-5">
              <SkeletonTable
                rowKey="id"
                columns={addPayrollColumns(setData)}
                data={activeUsers}
                isLoading={isLoading}
              />
            </div>

            <div className="flex gap-2 mx-5 mb-5">
              <LoadableButton
                className="btn btn-primary !px-4"
                onClick={handleUpdate}
                disabled={!data?.length || !range?.length}
                isLoading={isUpdateLoading}
                loadingLable="Adding Payroll..."
              >
                Save Payroll Details
              </LoadableButton>
            </div>
          </>
        )}

        <ConfirmationModal
          isOpen={isStatusModalOpen}
          setIsOpen={setIsStatusModalOpen}
          message={`Are you sure you want to set this employee as ${statusChangeInfo?.newStatus}?`}
          onConfirm={() => handleStatusConfirm(statusChangeInfo)}
          isLoading={statusLoading}
          loadingLabel="Updating Status..."
          type="warning"
        />
      </div>
    </>
  );
};

export default PayRoll;
